import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from "@mui/material";
import { useChartStore } from "@/stores/chartStore";
import { useDataBankSlots } from "@/hooks/useDataBankSlots";

/**
 * Guards against silently discarding unsaved birth-chart edits. Two intents feed
 * it, mutually exclusive: a LOAD (`useDataBankSlots.loadRecordIntoBirth` stashes
 * the pending record in `pendingBirthLoad` when the birth slot is dirty) and a
 * NEW CHART (the identity chip sets `pendingNewChart` when the slot is dirty).
 * Mounted once, app-wide; Cancel keeps the edits, and the chip's Update /
 * Save-as-copy remain the non-destructive alternatives.
 */
export const DiscardEditsDialog = () => {
	const pendingLoad = useChartStore((s) => s.pendingBirthLoad);
	const setPendingLoad = useChartStore((s) => s.setPendingBirthLoad);
	const pendingNew = useChartStore((s) => s.pendingNewChart);
	const setPendingNew = useChartStore((s) => s.setPendingNewChart);
	const { forceLoadRecordIntoBirth } = useDataBankSlots();

	// Load wins if both are somehow set; otherwise the new-chart intent shows.
	const mode: "load" | "new" | null = pendingLoad ? "load" : pendingNew ? "new" : null;

	const close = () => {
		setPendingLoad(null);
		setPendingNew(false);
	};
	const confirm = () => {
		if (pendingLoad) {
			forceLoadRecordIntoBirth(pendingLoad);
		} else if (pendingNew) {
			// Blank the birth slot AND clear the dial so the host returns to its
			// input card — the same reset the chip's clean "New chart" path runs.
			const s = useChartStore.getState();
			s.resetBirthInfo();
			s.setChartLabel("");
			s.clearComputedChart();
		}
		close();
	};

	return (
		<Dialog open={!!mode} onClose={close} maxWidth="xs" fullWidth>
			<DialogTitle>
				{mode === "new" ? "Start a new chart?" : "Discard unsaved edits?"}
			</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{mode === "new"
						? "Starting a new chart clears the current chart and any unsaved edits. Update or save them first to keep them."
						: `Loading ${
								pendingLoad?.name || "this chart"
						  } will replace the unsaved changes on the current chart. Update or save them first to keep them.`}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={close}>Cancel</Button>
				<Button onClick={confirm} color="error" variant="contained">
					{mode === "new" ? "Discard & start" : "Discard & load"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DiscardEditsDialog;
